import { DocumentType, Invoice, ProjectCheckConfig, ProjectCheckResult } from '../types';
import {
    fetchReportHistoryList,
    loadProjectById,
    saveProjectToCloud,
} from './cloudService';
import { getNormalizedServices } from './projectLv';
import { runProjectCheck, IssuedPeriod, ProjectCheckInput } from './projectCheckLogic';
import { openRetentionCents } from './advanceInvoiceLogic';
import { fetchProjectInvoices, fetchReservations, reservationsForProject } from './invoiceService';
import { fetchRetentionBackfill } from './retentionBackfill';
import { supabase } from './supabaseClient';
import { istSifaProjekt } from './projectType';
import type { SupabaseClient } from '@supabase/supabase-js';

/**
 * Beschafft die Eingaben des Projektchecks.
 *
 * Bewusst getrennt von projectCheckLogic: dort steht kein einziger
 * Datenbankzugriff, damit die 19 Regeln ohne Netz und ohne Mocks getestet
 * werden koennen. Hier steht ausschliesslich das Einsammeln.
 */

/**
 * Der Datenbankzugang dieses Moduls.
 *
 * Vorgabe ist der App-Client — jede bestehende Aufrufstelle bleibt unveraendert.
 * Mitgeben muss ihn nur, wer OHNE angemeldeten Benutzer prueft: der Nachtlauf
 * `scripts/integrity-sweep.ts` faehrt mit dem Service-Key, weil `auth.uid()`
 * dort null ist und RLS ihm sonst kein einziges Projekt zeigte.
 *
 * Der Sinn des Durchreichens ist, dass der Lauf DIESELBE Beschaffung benutzt
 * wie die Oberflaeche. Eine zweite Ladelogik daneben waere eine zweite Antwort
 * auf dieselbe Frage — und damit die Fehlerquelle, die der Waechter suchen soll.
 */
type Db = SupabaseClient<any, any, any>;

/**
 * Schreibt eine Aenderung an `metadata.globalData` des Projekts aus dem
 * Rechnungsmodul heraus — immer ueber den frisch geladenen Stand und den
 * regulaeren Speicherpfad, nie als Spalten-Update auf den Blob (ein solches
 * ueberschriebe parallele Aenderungen am Projekt).
 *
 * Speichert ohne Konflikt-Anker: seit dem Ausbau der optimistischen Sperre
 * gilt fuer jeden Schreibpfad last write wins.
 */
const patchProjectGlobalData = async (
    projectId: string,
    mutate: (global: Record<string, any>) => Record<string, any>,
): Promise<void> => {
    const project = await loadProjectById(projectId);
    if (!project) throw new Error('Das Projekt konnte nicht geladen werden.');

    const report = (project as any).data;
    const meta = report?.metadata ?? {};

    const nextReport = {
        ...report,
        metadata: {
            ...meta,
            globalData: mutate({ ...(meta.globalData ?? {}) }),
        },
    };

    await saveProjectToCloud(nextReport, projectId);
};

/**
 * Setzt oder loescht das Kennzeichen "fuer dieses Projekt entstehen keine
 * Berichte" und schaltet damit die berichtsbezogenen Regeln 1 und 2 ab.
 *
 * Bewusst am Projekt und nicht als globale Einstellung: `enableReportCount`
 * gilt fuer alle Projekte, und die Pruefung dort abzuschalten, wo sie traegt,
 * um sie hier loszuwerden, waere ein schlechter Tausch.
 */
export const setProjectReportExemption = async (
    projectId: string,
    exempt: boolean,
): Promise<void> => {
    await patchProjectGlobalData(projectId, (global) => ({ ...global, reportExempt: exempt }));
};

/**
 * Vertraglich vereinbarter Sicherheitseinbehalt des Projekts — die Vorbelegung
 * fuer jeden neuen Beleg (InvoiceEditor) und fuer den Rechnungslauf
 * (billingRunService).
 *
 * Gepflegt wird er im Rechnungsmodul und nicht im Projekt-Hub: er ist eine
 * Abrechnungsbedingung, keine Baustellenangabe, und den Aussendienst geht er
 * nichts an — Geldbetraege sieht nur der Innendienst.
 *
 * `undefined` entfernt den Eintrag, statt eine 0 zu schreiben. Eine 0 im
 * Projekt saehe aus wie die ausdrueckliche Vereinbarung "kein Einbehalt" —
 * gemeint ist aber "nichts vereinbart", und nur so bleibt unterscheidbar, ob
 * die Frage schon beantwortet wurde.
 */
export const setProjectRetentionPercent = async (
    projectId: string,
    percent: number | undefined,
): Promise<void> => {
    await patchProjectGlobalData(projectId, (global) => {
        const next = { ...global };
        if (percent === undefined) delete next.retention;
        else next.retention = { percent };
        return next;
    });
};

/** Leistungszeitraeume bereits ausgestellter Rechnungen eines Projekts. */
const fetchIssuedPeriods = async (
    projectId: string,
    excludeInvoiceId?: string,
    db: Db = supabase,
): Promise<IssuedPeriod[]> => {
    const { data, error } = await db
        .from('invoices')
        .select('id, invoice_number, document_type, delivery_date_from, delivery_date_to')
        .eq('primary_project_id', projectId)
        .in('status', ['ISSUED', 'SENT', 'PARTIALLY_PAID', 'PAID', 'PARTIALLY_CREDITED'])
        // Nur Belege, die einen Leistungszeitraum WIRKLICH beanspruchen.
        //
        // Ein Storno und eine Gutschrift decken nichts ab — sie nehmen zurueck.
        // Der Ursprungsbeleg faellt bereits ueber seinen Status CANCELLED
        // heraus; zaehlte der Stornobeleg mit, meldete der Check ausgerechnet
        // nach einer sauberen Rueckabwicklung eine Ueberschneidung mit dem
        // Beleg, der sie aufgehoben hat.
        //
        // Eine Korrekturrechnung haengt an einem Ursprungsbeleg, der den
        // Zeitraum schon nennt; sie mitzuzaehlen meldete denselben Zeitraum
        // zweimal. Und ein Abschlag ist eine Zahlungsanforderung auf den
        // Gesamtauftrag und benennt gar keine Leistungsperiode.
        .in('document_type', ['INVOICE', 'FINAL_INVOICE', 'REMAINING_INVOICE']);
    if (error) throw error;
    return (data || [])
        .filter((r) => r.id !== excludeInvoiceId)
        .map((r) => ({
            invoiceNumber: r.invoice_number ?? undefined,
            from: r.delivery_date_from ?? undefined,
            to: r.delivery_date_to ?? undefined,
        }));
};

/**
 * Die Archivberichte, die ausdruecklich NICHT abgerechnet werden
 * (`report_history.billing_exempt`, gesetzt ueber „Nicht abrechenbar" bzw.
 * set_report_billing_exempt).
 *
 * Eine eigene schmale Abfrage statt einer Spalte mehr in
 * `fetchReportHistoryList`: jene Liste speist auch Archivansicht und PDF, und
 * cloudService ist fuer diesen Vorgang gesperrt (siehe unten). Fail-open zur
 * strengen Seite: scheitert die Abfrage (etwa eine Umgebung ohne die Spalte),
 * gilt kein Bericht als ausgenommen und die Regeln urteilen wie bisher.
 */
const fetchBillingExemptReportIds = async (projectId: string, db: Db = supabase): Promise<Set<string>> => {
    try {
        const { data, error } = await db
            .from('report_history')
            .select('id')
            .eq('project_id', projectId)
            .eq('billing_exempt', true);
        if (error) return new Set();
        return new Set((data || []).map((r: { id: string }) => r.id));
    } catch {
        return new Set();
    }
};

export interface BuildCheckInputOptions {
    documentType: DocumentType;
    periodFrom?: string;
    periodTo?: string;
    /** Die eigene Rechnung zaehlt nicht als Ueberschneidung mit sich selbst. */
    excludeInvoiceId?: string;
    advancesCents?: number;
    /**
     * Was der zu pruefende ENTWURF an frueheren Sicherheitseinbehalten freigibt
     * (`InvoiceTotals.retentionReleaseCents`).
     *
     * Kommt vom Aufrufer und nicht aus einer Abfrage: der Wert steht am Entwurf
     * im Editor und ist dort aktueller als alles, was die Datenbank kennt —
     * genau wie `advancesCents`. Die Gegenzahl (was ueberhaupt noch einbehalten
     * ist) beschafft diese Datei dagegen selbst, weil sie ueber das PROJEKT
     * geht und der Aufrufer sie nicht haben kann.
     */
    retentionReleaseCents?: number;
    /**
     * Kumulative Abrechnung.
     *
     * Dann ueberschneidet sich der Zeitraum zwangslaeufig mit jedem frueheren
     * Beleg — er reicht immer vom Projektbeginn bis zum Stichtag. Die
     * Ueberschneidungspruefung waere dort eine Warnung vor dem Normalfall.
     */
    cumulative?: boolean;
}

/**
 * `db` ist optional und hat als Vorgabe den App-Client; ohne Argument aendert
 * sich nichts.
 *
 * WICHTIG UND UNVOLLSTAENDIG: durchgereicht wird er nur an die Abfrage, die in
 * DIESER Datei steht. `loadProjectById`/`fetchReportHistoryList` (cloudService)
 * und `fetchReservations` (invoiceService) halten ihren eigenen Modul-Client
 * fest; beide Dateien sind Bug-Magnete und fuer diesen Vorgang gesperrt. Der
 * Nachtlauf loest das, indem er den App-Client selbst mit dem Service-Key
 * baut (siehe Kopf von scripts/integrity-sweep.ts) — nicht, indem hier eine
 * zweite Ladelogik entsteht.
 */
export const buildProjectCheckInput = async (
    projectId: string,
    options: BuildCheckInputOptions,
    db: Db = supabase,
): Promise<ProjectCheckInput | null> => {
    // LESEMODUS. Ohne `skipMigration` heilt loadProjectById beim Lesen die
    // Base64-Bilder der Maengel und SCHREIBT dabei (cloudService,
    // convertBase64ImagesToUrls: Upload nach Storage plus update auf
    // inspection_items). Fuer eine Diagnose ist das falsch, und im Nachtlauf
    // waere es ein flottenweiter Umbau von Bestandsdaten mit dem Service-Key
    // an RLS und Schreibschutz vorbei — in einem Lauf, der sich "misst nur"
    // nennt und ein --dry-run anbietet.
    const project = await loadProjectById(projectId, undefined, { skipMigration: true });
    if (!project) return null;

    const meta = (project as any).data?.metadata ?? {};
    const global = meta.globalData ?? {};

    const [reportHistory, reservations, issuedPeriods, projectInvoices, backfill, exemptReportIds] = await Promise.all([
        fetchReportHistoryList(projectId),
        // Der eigene Beleg darf sich nicht selbst blockieren: seine Zeilen
        // stehen als Reservierung in der Sicht, und ohne diese Ausnahme meldet
        // der Check fuer den offenen Entwurf "Keine offene Position".
        fetchReservations([projectId], options.excludeInvoiceId),
        fetchIssuedPeriods(projectId, options.excludeInvoiceId, db),
        // Die vollstaendigen Belege des Projekts — fuer die
        // Sicherheitseinbehalte (docs/sicherheitseinbehalt-plan.md §10).
        //
        // Eine ZWEITE Abfrage auf dieselbe Tabelle, und das ist beabsichtigt:
        // `fetchIssuedPeriods` holt vier Spalten und filtert auf
        // INVOICE/FINAL_INVOICE/REMAINING_INVOICE — ausgerechnet die
        // ABSCHLAGSRECHNUNGEN, in denen der Einbehalt fast immer steckt, sind
        // dort ausgeschlossen, und `totals`/`retention_releases` stehen gar
        // nicht in der Auswahl. Sie zu erweitern hiesse, ihren Filter fuer
        // einen fremden Zweck aufzuweichen; die Zeitraumpruefung wuerde damit
        // falsch (siehe die Begruendung oben an der Abfrage).
        fetchProjectInvoices(projectId),
        // Die Alt-Einbehalte (Nr. 107): was der Auftraggeber auf Belege vor
        // dem Feature einbehalten hat, kennt kein Beleg — offen ist es
        // trotzdem. Fail-open, solange nur die Tabelle fehlt.
        fetchRetentionBackfill(projectId),
        fetchBillingExemptReportIds(projectId, db),
    ]);

    return {
        projectId,
        projectName: (project as any).name ?? meta.projectName ?? projectId,
        projectNumber: (project as any).project_number ?? meta.projectNumber,
        projectStatus: (project as any).status ?? global.status,
        customerId: (project as any).customer_id ?? undefined,
        reportExempt: global.reportExempt === true,
        // SiFa-Dauerbetreuung: Regeln 9/10/14 (Startdokumente, Abschluss vor
        // Schlussrechnung, stille Position) gelten nicht (docs/sifa-betreuung-plan.md).
        sifa: istSifaProjekt(meta),
        startDate: global.startDate,

        services: getNormalizedServices(project),
        serviceLog: Array.isArray(global.serviceLog) ? global.serviceLog : [],
        notes: Array.isArray(global.notes) ? global.notes : [],

        reportHistory: reportHistory.map((r: any) => ({
            inspectionNumber: r.inspectionNumber,
            date: r.date,
            ...(exemptReportIds.has(r.id) ? { billingExempt: true } : {}),
        })),
        currentInspectionNumber: meta.inspectionNumber,
        currentReportDate: meta.date,
        currentReportSectionId: meta.sectionId ?? null,

        vorankuendigung: meta.vorankuendigung
            ? {
                  history: meta.vorankuendigung.history,
                  archive: meta.vorankuendigung.archive,
              }
            : undefined,

        linkedProjectConfig: global.linkedProjectConfig,
        reservations: reservationsForProject(reservations, projectId),
        issuedPeriods,
        advancesCents: options.advancesCents,

        // Offene Sicherheitseinbehalte des Projekts.
        //
        // Die Auswahlregel wird NICHT nachgebaut: `openRetentionCents` ist
        // bereits die eine Antwort auf die Frage "was liegt beim
        // Auftraggeber" — Statusfilter, uebersprungene Stornobelege, der
        // Nettoeinbehalt (retention minus release, deshalb ergibt eine
        // freigebende Schlussrechnung 0) und der Backfill stehen dort. Eine
        // zweite Formulierung derselben Regel waere die Fehlerklasse, gegen die
        // dieses Modul gebaut ist.
        //
        // `excludeInvoiceId` wie ueberall sonst: der gepruefte Entwurf sperrt
        // sich nicht selbst. Zaehlte er mit, meldete der Check fuer eine
        // Schlussrechnung, die die Einbehalte gerade freigibt, es gaebe keine
        // mehr — und Regel 18 koennte nie greifen.
        openRetentionCents: openRetentionCents(projectInvoices, backfill, options.excludeInvoiceId),
        retentionReleaseCents: options.retentionReleaseCents,

        documentType: options.documentType,
        periodFrom: options.periodFrom,
        periodTo: options.periodTo,
        cumulative: options.cumulative,
    };
};

/**
 * Prueft alle Projekte, die in einer Rechnung vorkommen.
 *
 * Bei der Sammelrechnung sind das mehrere; jedes wird einzeln geprueft, damit
 * der Befund benennen kann, welches Projekt gemeint ist.
 */
export const checkInvoiceProjects = async (
    invoice: Pick<
        Invoice,
        | 'id'
        | 'lines'
        | 'primaryProjectId'
        | 'documentType'
        | 'deliveryDateFrom'
        | 'deliveryDateTo'
        | 'details'
        // Bewusst OPTIONAL angehaengt statt in die Pick-Liste: ein Aufrufer
        // ohne Summen (Testdoppel, Teilbeleg im Aufbau) soll weiter pruefen
        // duerfen. Fehlt der Wert, gilt "gibt nichts frei" — dieselbe Lesart
        // wie bei einem Entwurf ohne Freigabe.
    > &
        Partial<Pick<Invoice, 'totals'>>,
    config?: ProjectCheckConfig,
    todayIso?: string,
): Promise<ProjectCheckResult[]> => {
    const projectIds = new Set<string>();
    if (invoice.primaryProjectId) projectIds.add(invoice.primaryProjectId);
    for (const line of invoice.lines || []) {
        if (line.kind === 'SERVICE' && line.projectId) projectIds.add(line.projectId);
    }
    // Kein Projektbezug ist ein legitimer Fall — eine Abschlagsrechnung ohne
    // Projekt, ein Storno, eine kaufmaennische Gutschrift. Das ist ausdruecklich
    // etwas anderes als "der Check ist ausgefallen" und muss deshalb ein
    // eigenes, gruenes Ergebnis liefern statt einer leeren Liste.
    if (projectIds.size === 0) {
        return [
            {
                traffic: 'green',
                findings: [],
                canCreateDraft: true,
                canIssue: true,
                requiresAcknowledgement: false,
            },
        ];
    }

    const today = todayIso ?? new Date().toISOString().slice(0, 10);
    const results: ProjectCheckResult[] = [];
    for (const projectId of projectIds) {
        let input: ProjectCheckInput | null = null;
        let failure: string | undefined;
        try {
            input = await buildProjectCheckInput(projectId, {
                documentType: invoice.documentType,
                periodFrom: invoice.deliveryDateFrom,
                periodTo: invoice.deliveryDateTo,
                excludeInvoiceId: invoice.id,
                retentionReleaseCents: invoice.totals?.retentionReleaseCents,
                cumulative: invoice.details?.billingMode === 'CUMULATIVE',
            });
        } catch (e) {
            failure = e instanceof Error ? e.message : 'Unbekannter Fehler';
        }

        // Ein nicht ladbares Projekt ist ein SPERRENDER Befund, kein
        // stillschweigendes Auslassen. loadProjectById liefert bei einem
        // Netzfehler dasselbe null wie bei "gibt es nicht"; wuerde man den Fall
        // ueberspringen, bliebe eine leere Ergebnisliste zurueck — und die sah
        // frueher aus wie "geprueft, nichts gefunden".
        if (!input) {
            results.push(unknownProjectResult(projectId, failure));
            continue;
        }
        results.push(runProjectCheck(input, today, config));
    }
    return results;
};

/** Befund fuer ein Projekt, das sich nicht pruefen liess. */
const unknownProjectResult = (projectId: string, message?: string): ProjectCheckResult => ({
    traffic: 'red',
    findings: [
        {
            id: `${projectId}_check_unavailable`,
            rule: 'customer_missing',
            type: 'customer_missing',
            title: 'Projekt konnte nicht geprüft werden',
            detail: message
                ? `Der Projektcheck ist fehlgeschlagen: ${message}`
                : 'Das Projekt konnte nicht geladen werden. Ohne Prüfung wird nicht abgerechnet.',
            severity: 'high',
            traffic: 'red',
            projectId,
            projectName: projectId,
            acknowledgeable: false,
        },
    ],
    canCreateDraft: true,
    canIssue: false,
    requiresAcknowledgement: false,
});

/**
 * Fasst mehrere Projektergebnisse zu einem zusammen. Die schlechteste Ampel
 * gewinnt.
 *
 * Eine LEERE Liste bedeutet "nicht geprueft", niemals "nichts gefunden". Ohne
 * diese Unterscheidung liefern some() false und every() true — das Ergebnis
 * waere gruen mit canIssue, und ein ausgefallener Check haette wie eine
 * bestandene Pruefung gewirkt.
 */
export const mergeCheckResults = (results: ProjectCheckResult[]): ProjectCheckResult => {
    if (results.length === 0) {
        return {
            traffic: 'red',
            findings: [],
            canCreateDraft: true,
            canIssue: false,
            requiresAcknowledgement: false,
        };
    }
    const findings = results.flatMap((r) => r.findings);
    const hasRed = results.some((r) => r.traffic === 'red');
    const hasYellow = results.some((r) => r.traffic === 'yellow');
    return {
        traffic: hasRed ? 'red' : hasYellow ? 'yellow' : 'green',
        findings,
        canCreateDraft: true,
        canIssue: results.every((r) => r.canIssue),
        requiresAcknowledgement: !hasRed && hasYellow,
        // "some", nicht "every": bei einer Sammelrechnung reicht ein einziges
        // ausgenommenes Projekt, damit der Hinweis faellig ist — sonst
        // verschwiegen die uebrigen Projekte, dass hier etwas stillgelegt wurde.
        reportExempt: results.some((r) => r.reportExempt),
        // Ebenfalls "some": rechnet auch nur ein Projekt der Sammelrechnung nach
        // Stunden ab, gilt die Vollaussage "Berichte passen zusammen" nicht mehr
        // fuer den ganzen Beleg.
        hoursOnly: results.some((r) => r.hoursOnly),
    };
};

/**
 * Read-only-Lauf ueber alle Projekte.
 *
 * Vor der ersten echten Rechnung soll sichtbar sein, wie viel Drift in den
 * Bestandsdaten steckt. Der Lauf schreibt nichts.
 */
export const sweepAllProjects = async (
    projectIds: string[],
    config?: ProjectCheckConfig,
    todayIso?: string,
    onProgress?: (done: number, total: number) => void,
): Promise<Array<{ projectId: string; result: ProjectCheckResult }>> => {
    const today = todayIso ?? new Date().toISOString().slice(0, 10);
    const out: Array<{ projectId: string; result: ProjectCheckResult }> = [];
    let done = 0;
    for (const projectId of projectIds) {
        try {
            const input = await buildProjectCheckInput(projectId, { documentType: 'INVOICE' });
            if (input) out.push({ projectId, result: runProjectCheck(input, today, config) });
        } catch (e) {
            // Ein unlesbares Projekt darf den Lauf nicht abbrechen — es ist
            // selbst ein Befund, und die uebrigen Projekte sollen trotzdem
            // geprueft werden.
            out.push({
                projectId,
                result: {
                    traffic: 'red',
                    findings: [
                        {
                            id: `${projectId}_load_failed`,
                            rule: 'customer_missing',
                            type: 'customer_missing',
                            title: 'Projekt konnte nicht geprüft werden',
                            detail: e instanceof Error ? e.message : 'Unbekannter Fehler',
                            severity: 'high',
                            traffic: 'red',
                            projectId,
                            projectName: projectId,
                            acknowledgeable: false,
                        },
                    ],
                    canCreateDraft: true,
                    canIssue: false,
                    requiresAcknowledgement: false,
                },
            });
        }
        done += 1;
        onProgress?.(done, projectIds.length);
    }
    return out;
};
